export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
};

export const formatMoney = (amount, currency = 'USD') => {
  const safeCurrency = currency === 'CLP' ? 'CLP' : 'USD';
  const locale = safeCurrency === 'CLP' ? 'es-CL' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: safeCurrency,
    minimumFractionDigits: 0
  }).format(Number(amount || 0));
};

export const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('es-CL').format(date);
};

// Paleta de estado reducida a 5 tonos semánticos: verde=aprobado/completado,
// ámbar=pendiente, rojo=rechazado, azul=informativo, gris=borrador/inactivo.
// La sub-etapa del flujo se comunica en el texto (getStatusText), no en el color.
export const getStatusColor = (status) => {
  const colors = {
    'pendiente': 'bg-warning-50 text-warning-700 border-warning-100',
    'pendiente_pm': 'bg-warning-50 text-warning-700 border-warning-100',
    'pendiente_area': 'bg-warning-50 text-warning-700 border-warning-100',
    'pendiente_compras': 'bg-warning-50 text-warning-700 border-warning-100',
    'pendiente_controller': 'bg-warning-50 text-warning-700 border-warning-100',
    'pendiente_compra': 'bg-warning-50 text-warning-700 border-warning-100',
    'en_licitacion': 'bg-info-50 text-info-700 border-info-100',
    'borrador': 'bg-neutral-100 text-neutral-600 border-neutral-200',
    'convertida': 'bg-success-50 text-success-700 border-success-100',
    'aprobada': 'bg-success-50 text-success-700 border-success-100',
    'enviada': 'bg-info-50 text-info-700 border-info-100',
    'recibida': 'bg-success-50 text-success-700 border-success-100',
    'rechazada': 'bg-danger-50 text-danger-700 border-danger-100'
  };
  return colors[status] || 'bg-neutral-100 text-neutral-600 border-neutral-200';
};

// Mismo mapeo que getStatusColor, pero devuelve el nombre del tono semántico
// ('success'|'warning'|'danger'|'info'|'neutral') para usar con <Badge tone=...>.
export const getStatusTone = (status) => {
  const tones = {
    'pendiente': 'warning',
    'pendiente_pm': 'warning',
    'pendiente_area': 'warning',
    'pendiente_compras': 'warning',
    'pendiente_controller': 'warning',
    'pendiente_compra': 'warning',
    'en_licitacion': 'info',
    'borrador': 'neutral',
    'convertida': 'success',
    'aprobada': 'success',
    'enviada': 'info',
    'recibida': 'success',
    'rechazada': 'danger'
  };
  return tones[status] || 'neutral';
};

export const getStatusText = (status) => {
  const texts = {
    'pendiente': 'Pendiente',
    'pendiente_pm': 'Pendiente Project Manager',
    'pendiente_area': 'Pendiente Área',
    'pendiente_compras': 'Pendiente Compras',
    'pendiente_controller': 'Pendiente Controller',
    'pendiente_compra': 'Pendiente Compra',
    'en_licitacion': 'En Licitación',
    'borrador': 'Borrador',
    'convertida': 'Convertida',
    'aprobada': 'Aprobada',
    'enviada': 'Enviada a proveedor',
    'recibida': 'Recibida', 
    'rechazada': 'Rechazada'
  };
  return texts[status] || status;
};

// Los roles son identidad, no estado: un solo tono neutro para todos
// (evita inventar una paleta decorativa por rol).
export const getRoleColor = () => 'bg-neutral-100 text-neutral-700';

export const getRoleText = (role) => {
  const texts = {
    'comprador': 'Comprador',
    'project_manager': 'Project Manager',
    'area_manager': 'Gerente de Área',
    'purchasing_manager': 'Gerente de Compras',
    'purchase_controller': 'Controller de Compras',
    'project_director': 'Director de Proyectos',
    'contract_manager': 'Contract Manager',
    'super_user': 'Super Usuario',
    'contador': 'Contadora Corporativa',
    'tesorero': 'Tesorero Corporativo',
    'auditor': 'Auditor Interno',
    'finance_admin': 'Administrativo de Finanzas',
    'abogado': 'Abogado Corporativo'
  };
  return texts[role] || role;
};

export const getProfileText = (profile) => {
  const texts = {
    operativo: 'Operativo',
    gerente: 'Gerente',
    director: 'Director',
    super_user: 'Super Usuario'
  };
  return texts[profile] || profile || 'Operativo';
};

export const calculateTotalInventoryValue = (inventory, locations) => {
  return inventory.reduce((total, item) => {
    const itemTotal = item.locations.reduce((itemSum, location) => {
      return itemSum + (location.quantity * item.unitValue);
    }, 0);
    return total + itemTotal;
  }, 0);
};

export const getInventoryByLocation = (inventory, locationId) => {
  return inventory.map(item => {
    const locationStock = item.locations.find(loc => loc.locationId === locationId);
    return {
      ...item,
      quantity: locationStock ? locationStock.quantity : 0,
      totalValue: locationStock ? locationStock.quantity * item.unitValue : 0
    };
  }).filter(item => item.quantity > 0);
};

const randomId = (prefix) => {
  const rand = Math.random().toString(36).slice(2, 8);
  const stamp = Date.now().toString(36);
  return `${prefix}-${rand}${stamp}`.toUpperCase();
};

export const formatFileSize = (bytes) => {
  if (!bytes || Number.isNaN(bytes)) return '0 KB';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

export const fileToAttachment = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      resolve({
        id: randomId('ATT'),
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString(),
        dataUrl: typeof reader.result === 'string' ? reader.result : '',
        source: 'upload'
      });
    };
    reader.onerror = () => reject(new Error('No fue posible leer el archivo adjunto.'));
    reader.readAsDataURL(file);
  });

export const filesToAttachments = async (fileList) => {
  if (!fileList || fileList.length === 0) return [];
  const files = Array.from(fileList);
  const uploads = await Promise.all(files.map((file) => fileToAttachment(file)));
  return uploads.filter(Boolean);
};
